import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import {
  Waves, Bell, Heart, BookOpen, Calendar,
  Map, Trophy, Check, ArrowLeft, Loader2, AlertCircle,
} from 'lucide-react';

type Cycle = 'monthly' | 'annual';

const FREE_FEATURES = [
  { icon: <Map className="w-4 h-4" />,    label: 'All SA surf breaks + forecasts' },
  { icon: <Waves className="w-4 h-4" />,  label: '3-day forecast' },
  { icon: <Trophy className="w-4 h-4" />, label: 'Competition tracker' },
  { icon: <Waves className="w-4 h-4" />,  label: 'Gear recommender' },
]

const PRO_FEATURES = [
  { icon: <Bell className="w-4 h-4" />,     label: 'Surf alerts — night-before push notifications' },
  { icon: <Heart className="w-4 h-4" />,    label: 'Favourite breaks' },
  { icon: <BookOpen className="w-4 h-4" />, label: 'Session logging' },
  { icon: <Calendar className="w-4 h-4" />, label: '10-day extended forecast' },
]

export default function SubscribePage() {
  const [cycle, setCycle] = useState<Cycle>('annual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { subscription, isPro, isLoading } = useSubscription()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const returnStatus = searchParams.get('status')
  const monthlyPrice  = 79
  const annualPrice   = 599
  const annualMonthly = Math.round(annualPrice / 12)

  const handleProCTA = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Please sign in to upgrade.')

      const res = await supabase.functions.invoke('create-payfast-checkout', {
        body: { cycle },
      })
      if (res.error) throw new Error(res.error.message)

      const { action, params } = res.data as {
        action: string
        params: Record<string, string>
      }

      // PayFast requires a form POST — build and submit dynamically
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = action
      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        form.appendChild(input)
      })
      document.body.appendChild(form)
      form.submit()
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="px-4 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-white/8 transition-colors text-slate-400">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Waves className="w-5 h-5 text-sky-400" />
          <span className="font-semibold text-white">Barrelcast Pro</span>
        </div>
      </div>

      <div className="px-4 pt-6 pb-24 max-w-sm mx-auto space-y-6">

        {returnStatus === 'success' && (
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 flex items-center gap-3">
            <Check className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <p className="text-emerald-300 font-medium text-sm">Payment received!</p>
              <p className="text-emerald-400/70 text-xs mt-0.5">Your Pro access will activate within a few seconds.</p>
            </div>
          </div>
        )}
        {returnStatus === 'cancelled' && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
            <p className="text-amber-300 text-sm">Payment cancelled. No charge was made.</p>
          </div>
        )}

        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">More surf,<br /><span className="text-sky-400">less guesswork.</span></h1>
          <p className="text-slate-400 text-sm leading-relaxed">Get alerted when it's on, log every session, and plan further ahead.</p>
        </div>

        <div className="flex items-center justify-center">
          <div className="bg-white/5 border border-white/10 rounded-xl p-1 flex gap-1">
            {(['monthly', 'annual'] as Cycle[]).map((c) => (
              <button key={c} onClick={() => setCycle(c)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${cycle === c ? 'bg-sky-500 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}>
                {c === 'monthly' ? 'Monthly' : 'Annual'}
                {c === 'annual' && <span className={`ml-1.5 text-xs font-semibold ${cycle === 'annual' ? 'text-white/80' : 'text-sky-400'}`}>−37%</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {/* Free card */}
          <div className="rounded-2xl border border-white/10 bg-white/3 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div><p className="font-semibold text-white">Free</p><p className="text-slate-400 text-xs mt-0.5">Always free</p></div>
              <p className="text-2xl font-bold text-slate-300">R0</p>
            </div>
            <div className="space-y-2">
              {FREE_FEATURES.map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <span className="text-slate-500 shrink-0">{icon}</span>{label}
                </div>
              ))}
            </div>
            {!isPro && !isLoading && (
              <div className="w-full py-2.5 rounded-xl border border-white/10 text-center text-sm text-slate-400 font-medium">Current plan</div>
            )}
          </div>

          {/* Pro card */}
          <div className="rounded-2xl border border-sky-500/50 bg-sky-500/5 p-5 space-y-4 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-sky-500/10 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-white">Pro</p>
                  <span className="text-xs bg-sky-500/20 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full font-medium">Best value</span>
                </div>
                <p className="text-slate-400 text-xs mt-0.5">{cycle === 'annual' ? `R${annualPrice}/year — billed once` : 'Billed monthly'}</p>
              </div>
              <p className="text-2xl font-bold text-white">R{cycle === 'annual' ? annualMonthly : monthlyPrice}<span className="text-sm font-normal text-slate-400">/mo</span></p>
            </div>

            <div className="space-y-2">
              <p className="text-xs text-slate-500 font-medium">Everything in Free, plus:</p>
              {PRO_FEATURES.map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <span className="text-sky-400 shrink-0">{icon}</span>{label}
                </div>
              ))}
            </div>

            {error && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-3 flex items-center gap-2 text-sm text-red-300">
                <AlertCircle className="w-4 h-4 shrink-0" />{error}
              </div>
            )}

            {isPro ? (
              <div className="w-full py-3 rounded-xl bg-sky-500/20 border border-sky-500/30 text-center text-sm text-sky-300 font-medium flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />You're on Pro
              </div>
            ) : (
              <button onClick={handleProCTA} disabled={loading || isLoading}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-400 active:bg-sky-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors shadow-lg shadow-sky-500/20 flex items-center justify-center gap-2">
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Redirecting to PayFast…</>
                  : cycle === 'annual' ? `Start Pro — R${annualPrice}/year` : `Start Pro — R${monthlyPrice}/month`}
              </button>
            )}

            {!isPro && <p className="text-center text-xs text-slate-600">Secure payment via PayFast · Cancel anytime</p>}
          </div>
        </div>

        {isPro && subscription?.currentPeriodEnd && (
          <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm text-slate-400">
            {subscription.cancelAtPeriodEnd
              ? <p>Pro access until <span className="text-white font-medium">{subscription.currentPeriodEnd.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</span>, then reverts to Free.</p>
              : <p>Renews on <span className="text-white font-medium">{subscription.currentPeriodEnd.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</span>.</p>
            }
          </div>
        )}

        <p className="text-center text-xs text-slate-600 leading-relaxed">Cancel anytime. No hidden fees. Prices in South African Rand (ZAR).</p>
      </div>
    </div>
  )
}
